/**
 * Helper Utilities
 * General-purpose helper functions used throughout the application.
 */

/**
 * Generates a unique identifier.
 * @returns A unique string ID.
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Creates a debounced version of a function.
 * @param fn - The function to debounce.
 * @param delay - Delay in milliseconds.
 * @returns Debounced function.
 */
export const debounce = <T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Creates a throttled version of a function.
 * @param fn - The function to throttle.
 * @param limit - Time limit in milliseconds.
 * @returns Throttled function.
 */
export const throttle = <T extends (...args: Parameters<T>) => void>(
  fn: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Deep clones an object.
 * @param obj - The object to clone.
 * @returns A deep copy of the object.
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Safely parses JSON with a fallback value.
 * @param jsonString - The JSON string to parse.
 * @param fallback - Fallback value if parsing fails.
 * @returns Parsed object or fallback.
 */
export const safeJsonParse = <T>(jsonString: string, fallback: T): T => {
  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return fallback;
  }
};

/**
 * Capitalizes the first letter of a string.
 * @param str - The string to capitalize.
 * @returns Capitalized string.
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Converts a string to kebab-case.
 * @param str - The string to convert.
 * @returns Kebab-cased string.
 */
export const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

/**
 * Picks specified keys from an object.
 * @param obj - The source object.
 * @param keys - Keys to pick.
 * @returns New object with only specified keys.
 */
export const pick = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  return keys.reduce(
    (result, key) => {
      if (key in obj) {
        result[key] = obj[key];
      }
      return result;
    },
    {} as Pick<T, K>
  );
};

/**
 * Omits specified keys from an object.
 * @param obj - The source object.
 * @param keys - Keys to omit.
 * @returns New object without specified keys.
 */
export const omit = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
};

/**
 * Delays execution for a specified time.
 * @param ms - Milliseconds to delay.
 * @returns Promise that resolves after delay.
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Checks if code is running in browser environment.
 * @returns True if running in browser.
 */
export const isBrowser = (): boolean => {
  return typeof window !== 'undefined';
};

/**
 * Gets query parameters from URL.
 * @param url - The URL string (defaults to current location).
 * @returns Object with query parameters.
 */
export const getQueryParams = (url?: string): Record<string, string> => {
  const searchParams = new URLSearchParams(
    url ? new URL(url).search : window.location.search
  );
  const params: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

/**
 * Generates an invoice number in format: INV-YYYY-XXXXX
 * @returns Invoice number string.
 */
export const generateInvoiceNumber = (): string => {
  const year = new Date().getFullYear();
  const timestamp = Date.now();
  // Use last 5 digits of timestamp for uniqueness
  const sequence = String(timestamp).slice(-5);
  return `INV-${year}-${sequence}`;
};

/**
 * Formats a date to DD/MM/YYYY format.
 * @param date - The date to format.
 * @returns Formatted date string.
 */
export const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

